import React, { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { AccountingApi } from '_api';
import { MODAL_KEYS } from '_constants/modals';
import { theme } from '_constants/theme';
import { SOURCE } from '_constants/transactions';
import { handleError, handleResponse } from '_helpers/api';
import { usePrevious } from '_hooks';
import { selectBankAccountItems, selectData, selectLoading } from '_store/bankAccounts/selector';
import { getAccounts } from '_store/bankAccounts/slice';
import { addPlaidAccount } from '_store/bankAccounts/slice';
import { showModal } from '_store/modals/slice';
import { selectActiveMainProductId, selectActiveMainProductSource } from '_store/order/selector';
import { selectCustomerId } from '_store/user/selector';

import Button from 'components/atomic/Button';
import PlaidLinkButton from 'components/atomic/PlaidLinkButton';
import Table from 'components/atomic/Table';
import Card from 'components/atomic/layouts/Card';
import Text from 'components/atomic/layouts/Text';

import BankAccountsList from './BankAccountsList';

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${theme.spaces.large}px;
  padding-right: ${theme.spaces.large}px;
  padding-left: ${theme.spaces.large}px;
`;

const StyledTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${theme.spaces.smallX}px;
`;

const StyledFooter = styled.div`
  padding-bottom: ${theme.spaces.large}px;
  padding-right: ${theme.spaces.large}px;
  padding-left: ${theme.spaces.large}px;
`;

const StyledBody = styled.div`
  padding-top: ${theme.spaces.medium}px;
  padding-bottom: ${theme.spaces.large}px;
  padding-left: ${theme.spaces.large}px;
  padding-right: ${theme.spaces.large}px;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  cursor: pointer;
`;

const StyledEmptyDataContainer = styled.div`
  padding-top: ${theme.spaces.medium}px;
`;

const getColumns = ({ handleAccountDelete }) => {
  return [
    {
      dataIndex: 'type',
      title: 'Type',
      width: '15%',
      render: (row) => (
        <Text size={theme.fontSizes.small} weight={400}>
          {row?.type}
        </Text>
      ),
    },
    {
      dataIndex: 'name',
      title: 'Name',
      width: '15%',
      render: (row) => (
        <Text size={theme.fontSizes.small} weight={400}>
          {row?.name}
        </Text>
      ),
    },
    {
      dataIndex: 'description',
      title: 'Description',
      width: '40%',
      render: (row) => (
        <Text size={theme.fontSizes.small} weight={400}>
          {row?.description}
        </Text>
      ),
    },
    {
      dataIndex: 'bankAccountNumber',
      title: 'Bank Account Number',
      width: '15%',
      render: (row) => (
        <Text size={theme.fontSizes.small} weight={400}>
          #{row?.bankAccountNumber}
        </Text>
      ),
    },
    {
      dataIndex: 'disconnect',
      title: null,
      width: '15%',
      render: (row) => (
        <StyledButtonContainer>
          <Button
            small
            outlined
            color={theme.colors.red.primary}
            onClick={() => {
              handleAccountDelete(row.id);
            }}
          >
            Disconnect
          </Button>
        </StyledButtonContainer>
      ),
    },
  ];
};

const BankAccountsView = () => {
  const dispatch = useDispatch();
  const customerId = useSelector(selectCustomerId);
  const productId = useSelector(selectActiveMainProductId);
  const prevProductId = usePrevious(productId);
  const loading = useSelector(selectLoading);
  const data = useSelector(selectData);
  const items = useSelector(selectBankAccountItems);
  const source = useSelector(selectActiveMainProductSource);
  const [token, setToken] = useState(null);

  const handleAccountAdd = (publicKey) => {
    dispatch(addPlaidAccount({ customerId, productId, publicKey }));
  };

  const handleAccountDelete = (accountId) => {
    dispatch(showModal({ key: MODAL_KEYS.bankAccountDelete, data: { accountId } }));
  };

  useEffect(() => {
    if (customerId && productId && prevProductId !== productId) {
      dispatch(getAccounts({ customerId, productId }));
    }
  }, [customerId, prevProductId, productId]);

  useEffect(() => {
    if (customerId && productId && !token) {
      (async function () {
        try {
          const response = await AccountingApi.getPlaidToken({ customerId, productId });
          const { link_token } = handleResponse(response);
          setToken(link_token);
        } catch (e) {
          const parsedError = handleError(e);
          toast.error(parsedError?.message || 'Something went wrong');
        }
      })();
    }
  }, [customerId, productId, token]);

  if (!source || source === SOURCE.xero) {
    return (
      <StyledCard>
        <StyledTitleContainer>
          <Text size={theme.fontSizes.medium} weight={600}>
            Transaction Source
          </Text>
          <Text size={theme.fontSizes.small}>Transactions will be sourced from Xero</Text>
        </StyledTitleContainer>
      </StyledCard>
    );
  }

  return (
    <StyledCard noPadding>
      <StyledHeader>
        <StyledTitleContainer>
          <Text size={theme.fontSizes.medium} weight={600}>
            Bank Accounts
          </Text>
          <Text size={theme.fontSizes.small}>
            Automatically import transactions securely from your Bank or Credit Card
          </Text>
        </StyledTitleContainer>
        {!isMobile && (
          <div>
            <PlaidLinkButton color={theme.colors.gray.primary} token={token} onSuccess={handleAccountAdd}>
              Connect New Account
            </PlaidLinkButton>
          </div>
        )}
      </StyledHeader>
      {!isMobile && (
        <StyledBody>
          <Table columns={getColumns({ handleAccountDelete })} rows={data} loading={loading} skeletonRowsNum={3} />
          {!data.length && (
            <StyledEmptyDataContainer>
              <Text size={theme.fontSizes.small} weight={400} color={theme.colors.gray.primary} centered>
                No data available
              </Text>
            </StyledEmptyDataContainer>
          )}
        </StyledBody>
      )}
      {isMobile && <BankAccountsList loading={loading} items={items} onDelete={handleAccountDelete} />}
      {isMobile && (
        <StyledFooter>
          <PlaidLinkButton color={theme.colors.gray.primary} token={token} onSuccess={handleAccountAdd}>
            Connect New Account
          </PlaidLinkButton>
        </StyledFooter>
      )}
    </StyledCard>
  );
};

export default BankAccountsView;
